/* eslint-disable react-hooks/exhaustive-deps */
// eslint-disable-next-line monorepo/no-internal-import
import {QueryObserverResult} from '@aboutyou/data-fetcher';
import {useSuspenseSafeHydrationDone} from '@aboutyou/lazy-imports';
import React, {FC, createContext, useContext, useMemo} from 'react';

interface CtxValue<T> {
  data?: T;
  error?: unknown;
  isError?: boolean;
  isIdle?: boolean;
  isStale?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  updateCache: (data: T) => void;
  refetch: () => Promise<QueryObserverResult<T, unknown>> | void;
}

interface Config<T> {
  defaultValue: T | undefined;
  useQueryHook: () => CtxValue<T>;
}

export const createDataStore = <T extends unknown>({
  defaultValue,
  useQueryHook,
}: Config<T>) => {
  const Context = createContext<CtxValue<T>>({
    data: defaultValue,
    error: undefined,
    isError: false,
    isIdle: false,
    isLoading: false,
    updateCache: () => void 0,
    refetch: () => void 0,
  });

  const useData = () => useContext(Context);

  const Provider: FC<React.PropsWithChildren> = ({children}) => {
    const request = useQueryHook();
    const isHydrationDone = useSuspenseSafeHydrationDone();
    const value = useMemo(
      () => ({
        data: request.data || defaultValue,
        error: request.error,
        isError: request.isError,
        isIdle: request.isIdle,
        isLoading: request.isLoading,
        isStale: request.isStale,
        isFetching: request.isFetching,
        updateCache: request.updateCache,
        refetch: request.refetch,
      }),
      [
        isHydrationDone
          ? [
              request.data,
              request.isLoading,
              request.error,
              request.isError,
              request.isIdle,
              request.isStale,
              request.isFetching,
              request.updateCache,
              request.refetch,
            ]
          : false,
      ],
    );
    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  return {
    useData,
    Provider,
  };
};
